import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/api/client";
import { errorMessage } from "@/api/errors";
import SearchStatusBar from "@/components/common/SearchStatusBar";
import BudgetEditDialog from "./BudgetEditDialog";
import { Checkbox } from "@/components/ui/checkbox";
import BatchActionBar from "@/features/batch/BatchActionBar";
import BatchCopyDialog from "@/features/batch/BatchCopyDialog";
import { useSelection } from "@/features/batch/useSelection";
import {
  type Campaign,
  type CampaignInsights,
  type DatePreset,
  DATE_PRESET_LABELS,
  statusBadge,
  formatMoney,
  formatCentsMoney,
  formatNumber,
} from "./types";

const STATUS_OPTIONS = [
  { value: "ALL",      label: "全部状态" },
  { value: "ACTIVE",   label: "ACTIVE" },
  { value: "PAUSED",   label: "PAUSED" },
  { value: "DELETED",  label: "DELETED" },
  { value: "ARCHIVED", label: "ARCHIVED" },
];

interface Props {
  accountId: string;
  currency?: string;
}

export default function CampaignsSection({ accountId, currency }: Props) {
  const qc = useQueryClient();
  const [range, setRange] = useState<DatePreset>("last_30d");
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("ALL");
  const [budgetEdit, setBudgetEdit] = useState<Campaign | null>(null);
  const [copyTarget, setCopyTarget] = useState<Campaign | null>(null);
  const sel = useSelection();

  // Base list: cheap, no insights. Renders the table immediately.
  const base = useQuery({
    queryKey: ["campaigns:base", accountId],
    queryFn: async () => {
      const { data, error, response } = await api.GET(
        "/api/v1/ad-accounts/{accountId}/campaigns",
        { params: { path: { accountId }, query: { withInsights: false } as never } }
      );
      if (error || !response.ok) throw error || new Error("加载失败");
      return data as unknown as Campaign[];
    },
    staleTime: 60_000,
  });

  // Insights map: slower (Meta-side aggregation). Fills metric cells when it lands.
  const insights = useQuery({
    queryKey: ["campaigns:insights", accountId, range],
    queryFn: async () => {
      const { data, error, response } = await api.GET(
        "/api/v1/ad-accounts/{accountId}/campaigns/insights" as never,
        { params: { path: { accountId }, query: { range } } } as never
      );
      if (error || !response.ok) throw error || new Error("加载失败");
      return data as unknown as Record<string, CampaignInsights>;
    },
    staleTime: 60_000,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["campaigns:base", accountId] });
    qc.invalidateQueries({ queryKey: ["campaigns:insights", accountId] });
    // Also bump combined keys used elsewhere (e.g. CampaignDetailPage).
    qc.invalidateQueries({ queryKey: ["campaigns", accountId] });
  };

  const toggleStatus = useMutation({
    mutationFn: async ({ id, current }: { id: string; current: string | null }) => {
      const next = current === "ACTIVE" ? "PAUSED" : "ACTIVE";
      const { error, response } = await api.PATCH(
        "/api/v1/ad-accounts/{accountId}/campaigns/{campaignId}/status" as never,
        { params: { path: { accountId, campaignId: id } }, body: { status: next } } as never
      );
      if (error || !response.ok) throw error || new Error("切换失败");
      return next;
    },
    onSuccess: (next) => {
      invalidate();
      toast.success(next === "ACTIVE" ? "已启用" : "已暂停");
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const copy = useMutation({
    mutationFn: async (args: { id: string; newStatus: string; startTime: string | null; endTime: string | null }) => {
      const { data, error, response } = await api.POST(
        "/api/v1/ad-accounts/{accountId}/campaigns/{campaignId}/copy",
        {
          params: { path: { accountId, campaignId: args.id } },
          body: {
            newStatus: args.newStatus,
            startTime: args.startTime,
            endTime: args.endTime,
          } as never,
        }
      );
      if (error || !response.ok) throw error || new Error("复制失败");
      return data as unknown as { newId: string };
    },
    onSuccess: (r) => {
      invalidate();
      toast.success(`复制成功，新 ID：${r.newId}`);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error, response } = await api.DELETE(
        "/api/v1/ad-accounts/{accountId}/campaigns/{campaignId}",
        { params: { path: { accountId, campaignId: id } } }
      );
      if (error || !response.ok) throw error || new Error("删除失败");
    },
    onSuccess: () => {
      invalidate();
      toast.success("已删除");
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const insightsLoading = insights.isLoading || insights.isFetching;

  const filtered = useMemo(() => {
    const rows = base.data ?? [];
    const needle = q.trim().toLowerCase();
    return rows.filter((c) => {
      if (status !== "ALL" && c.status !== status) return false;
      if (needle && !((c.name ?? "") + " " + c.id).toLowerCase().includes(needle)) return false;
      return true;
    });
  }, [base.data, q, status]);

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="font-medium text-lg">Campaigns</h2>
        <div className="flex items-center gap-1 text-xs">
          {(["today", "last_7d", "last_30d", "maximum"] as DatePreset[]).map((p) => (
            <button
              key={p}
              onClick={() => setRange(p)}
              className={`px-3 py-1.5 rounded-md transition-colors ${
                range === p
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted"
              }`}
            >
              {DATE_PRESET_LABELS[p]}
            </button>
          ))}
          <button
            onClick={invalidate}
            className="ml-2 px-3 py-1.5 text-muted-foreground hover:underline"
          >
            刷新
          </button>
        </div>
      </div>

      <SearchStatusBar
        q={q}
        onQChange={setQ}
        status={status}
        onStatusChange={setStatus}
        statusOptions={STATUS_OPTIONS}
        placeholder="搜索 Campaign 名称或 ID"
      />

      {sel.count > 0 && (
        <BatchActionBar
          accountId={accountId}
          level="campaigns"
          resourceLabel="Campaign"
          selectedIds={sel.ids}
          onClear={sel.clear}
          onSuccess={invalidate}
        />
      )}

      {budgetEdit && (
        <BudgetEditDialog
          level="campaign"
          accountId={accountId}
          targetId={budgetEdit.id}
          currentDailyCents={budgetEdit.dailyBudgetCents}
          currentLifetimeCents={budgetEdit.lifetimeBudgetCents}
          currency={currency}
          onClose={() => setBudgetEdit(null)}
        />
      )}

      {copyTarget && (
        <BatchCopyDialog
          open={!!copyTarget}
          onOpenChange={(o) => { if (!o) setCopyTarget(null); }}
          count={1}
          resourceLabel="Campaign"
          onSubmit={({ newStatus, startTime, endTime }) => {
            copy.mutate({ id: copyTarget.id, newStatus, startTime, endTime });
            setCopyTarget(null);
          }}
        />
      )}

      <div className="rounded-lg border border-border bg-background overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted text-muted-foreground text-xs">
            <tr>
              <th className="text-left px-3 py-2 font-medium w-8">
                <Checkbox
                  aria-label="全选"
                  checked={
                    filtered.length > 0 && filtered.every((c) => sel.has(c.id))
                      ? true
                      : filtered.some((c) => sel.has(c.id))
                        ? "indeterminate"
                        : false
                  }
                  onCheckedChange={(v) => {
                    if (v === true) sel.setAll(filtered.map((c) => c.id));
                    else sel.clear();
                  }}
                />
              </th>
              <th className="text-left px-3 py-2 font-medium w-12">开关</th>
              <th className="text-left px-3 py-2 font-medium min-w-[260px]">广告系列</th>
              <th className="text-right px-3 py-2 font-medium">预算</th>
              <th className="text-right px-3 py-2 font-medium">花费</th>
              <th className="text-right px-3 py-2 font-medium">订单数</th>
              <th className="text-right px-3 py-2 font-medium">CPA</th>
              <th className="text-right px-3 py-2 font-medium">CPC</th>
              <th className="text-right px-3 py-2 font-medium">加购</th>
              <th className="text-right px-3 py-2 font-medium">结账</th>
              <th className="text-right px-3 py-2 font-medium">CPM</th>
              <th className="text-right px-3 py-2 font-medium">操作</th>
            </tr>
          </thead>
          <tbody>
            {base.isLoading && (
              <tr><td colSpan={12} className="px-4 py-6 text-center text-muted-foreground">加载中…</td></tr>
            )}
            {!base.isLoading && base.data?.length === 0 && (
              <tr><td colSpan={12} className="px-4 py-6 text-center text-muted-foreground">
                该账户尚无 Campaign（可在 Ads Manager 创建，或用沙盒"造数据"）
              </td></tr>
            )}
            {!base.isLoading && (base.data?.length ?? 0) > 0 && filtered.length === 0 && (
              <tr><td colSpan={12} className="px-4 py-6 text-center text-muted-foreground">
                没有符合筛选条件的 Campaign
              </td></tr>
            )}
            {filtered.map((c) => {
              const ins = insights.data?.[c.id];
              const isActive = c.status === "ACTIVE";
              return (
                <tr key={c.id} className="border-t border-border hover:bg-muted/30 transition-colors">
                  <td className="px-3 py-2">
                    <Checkbox
                      aria-label={`选择 ${c.name ?? c.id}`}
                      checked={sel.has(c.id)}
                      onCheckedChange={() => sel.toggle(c.id)}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <button
                      onClick={() => toggleStatus.mutate({ id: c.id, current: c.status })}
                      disabled={toggleStatus.isPending}
                      title={isActive ? "暂停" : "启用"}
                      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                        isActive ? "bg-emerald-500" : "bg-gray-300"
                      } ${toggleStatus.isPending ? "opacity-50" : ""}`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                          isActive ? "translate-x-4" : "translate-x-0.5"
                        }`}
                      />
                    </button>
                  </td>
                  <td className="px-3 py-2">
                    <Link
                      to={`/ad-accounts/${accountId}/campaigns/${c.id}`}
                      className="block text-primary hover:underline font-medium"
                    >
                      {c.name ?? "(无名称)"}
                    </Link>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`text-xs px-1.5 py-0.5 rounded ${statusBadge(c.status)}`}>
                        {c.status ?? "—"}
                      </span>
                      {c.objective && (
                        <span className="text-xs text-muted-foreground">{c.objective}</span>
                      )}
                    </div>
                    <div className="text-xs font-mono text-muted-foreground/70 mt-0.5">{c.id}</div>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <div>{formatCentsMoney(c.dailyBudgetCents, currency)}</div>
                    <div className="text-xs text-muted-foreground">
                      {c.dailyBudgetCents != null ? "日预算" : c.lifetimeBudgetCents != null ? "总预算" : ""}
                    </div>
                  </td>
                  <Metric loading={insightsLoading} value={ins?.spend} fmt={(v) => formatMoney(v, currency)} />
                  <Metric loading={insightsLoading} value={ins?.purchase} fmt={formatNumber} />
                  <Metric loading={insightsLoading} value={ins?.cpa} fmt={(v) => formatMoney(v, currency)} />
                  <Metric loading={insightsLoading} value={ins?.cpc} fmt={(v) => formatMoney(v, currency)} />
                  <Metric loading={insightsLoading} value={ins?.addToCart} fmt={formatNumber} />
                  <Metric loading={insightsLoading} value={ins?.initiateCheckout} fmt={formatNumber} />
                  <Metric loading={insightsLoading} value={ins?.cpm} fmt={(v) => formatMoney(v, currency)} />
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <Link
                      to={`/ad-accounts/${accountId}/campaigns/${c.id}`}
                      className="text-xs text-primary hover:underline mr-3"
                    >
                      查看
                    </Link>
                    <button
                      onClick={() => setBudgetEdit(c)}
                      className="text-xs text-primary hover:underline mr-3"
                    >
                      改预算
                    </button>
                    <button
                      onClick={() => setCopyTarget(c)}
                      disabled={copy.isPending}
                      className="text-xs text-primary hover:underline mr-3 disabled:opacity-50"
                    >
                      复制
                    </button>
                    <button
                      onClick={() => { if (confirm(`确定删除 "${c.name}"?`)) remove.mutate(c.id); }}
                      disabled={remove.isPending}
                      className="text-xs text-destructive hover:underline disabled:opacity-50"
                    >
                      删除
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function Metric({ loading, value, fmt }: { loading: boolean; value: unknown; fmt: (v: never) => string }) {
  if (value == null) {
    return (
      <td className="px-3 py-2 text-right tabular-nums text-muted-foreground/50">
        {loading ? "…" : "—"}
      </td>
    );
  }
  return <td className="px-3 py-2 text-right tabular-nums">{fmt(value as never)}</td>;
}
